/**
 * Script INGEST PIPELINE (Agent 01, owned here, consumed by the Script
 * Editor's inline upload too).
 *
 *   PDF file → pdf.js text → Worker `/chat` (Claude normalises to Final
 *   Draft + isolates structured elements) → ParsedScript → structured
 *   `ParsedScriptIngest` written atomically into StudioStore
 *   (`ingestParsedScript`), which auto-creates the cast / locations /
 *   assets the parser found (living-document, _GLOBAL.md G6).
 *
 * Extracted out of `ScriptUploadPanel` so the same battle-tested pipe
 * powers both the dedicated upload panel and the in-page "drop a PDF"
 * affordance on the empty Script view.
 */

import type {
  ParsedScriptIngest,
  StudioAsset,
  StudioStore,
} from "../StudioStore";
import { bodyToElements } from "./scriptModel";

/** Hard cap on the upload — large screenplays still fit comfortably. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MB
/** Cap on extracted text we send to the model (≈ a long feature). */
const MAX_TEXT_CHARS = 200_000;
/** Model used for the structured extraction call. */
const EXTRACTION_MODEL = "claude-sonnet-4-6";
/** Token budget for the structured JSON the model returns. */
const EXTRACTION_MAX_TOKENS = 8192;

/** Shape Claude is asked to return. Validated defensively on parse. */
export interface ParsedScript {
  scenes: Array<{ heading?: string; body?: string }>;
  characters: Array<{ name?: string; description?: string }>;
  locations: Array<{ name?: string; description?: string }>;
  assets: Array<{ name?: string; type?: string; description?: string }>;
}

export interface IngestCounts {
  scenes: number;
  characters: number;
  locations: number;
  assets: number;
}

export const EXTRACTION_SYSTEM_PROMPT = `You are a screenplay parser for a film studio app. You receive the raw, often messy text extracted from a screenplay PDF and you return ONLY a single JSON object (no prose, no markdown fences) with this exact shape:

{
  "scenes": [{ "heading": "INT. KITCHEN - DAY", "body": "Action and dialogue, one element per line. Dialogue formatted as NAME: line." }],
  "characters": [{ "name": "MEI", "description": "one-line description inferred from the script" }],
  "locations": [{ "name": "KITCHEN", "description": "short description" }],
  "assets": [{ "name": "vintage camera", "type": "prop", "description": "short note" }]
}

Rules:
- Split the script into scenes at slug lines (INT./EXT./scene headings). Each scene's "heading" is the slug line; "body" is the cleaned action + dialogue.
- In each scene body, put every speech on its own line as "NAME: dialogue" and every action beat on its own line. Strip page numbers, headers, footers, and PDF artifacts.
- "characters" is the UNIQUE set of speaking/named characters across the whole script.
- "locations" is the UNIQUE set of distinct settings.
- "assets" is notable props/vehicles/set pieces/wardrobe. "type" must be one of: prop, vehicle, set_piece, wardrobe, other.
- If the text is not actually a screenplay or has no scenes, return {"scenes":[],"characters":[],"locations":[],"assets":[]}.
- Return STRICT JSON only. Do not wrap it in code fences.`;

/**
 * Extract concatenated text from every page using pdf.js (lazy-imported
 * so the studio bundle stays lean until a script is actually uploaded).
 */
export async function extractPdfText(file: File): Promise<string> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).href;

  const buffer = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(buffer) });
  const doc = await loadingTask.promise;
  const pageTexts: string[] = [];
  try {
    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
      const page = await doc.getPage(pageNumber);
      const content = await page.getTextContent();
      const pageText = content.items
        .map((item) => ("str" in item ? item.str : ""))
        .join(" ");
      pageTexts.push(pageText);
      page.cleanup();
    }
  } finally {
    await loadingTask.destroy();
  }
  return pageTexts.join("\n\n").replace(/[ \t]{2,}/g, " ").trim();
}

/** Clamp the extracted text and report whether we trimmed it. */
export function clampText(text: string): { text: string; truncated: boolean } {
  if (text.length <= MAX_TEXT_CHARS) return { text, truncated: false };
  return { text: text.slice(0, MAX_TEXT_CHARS), truncated: true };
}

/**
 * Ask Claude (via the Worker `/chat` passthrough) to normalise the raw
 * screenplay text and return structured JSON. NON-streaming so we read
 * one clean JSON body back rather than parsing SSE.
 */
export async function analyzeScript(
  scriptText: string,
  workerBaseUrl: string,
): Promise<ParsedScript> {
  if (!workerBaseUrl) {
    throw new Error("No worker URL configured — can't reach Koko's parser.");
  }
  const body = {
    model: EXTRACTION_MODEL,
    max_tokens: EXTRACTION_MAX_TOKENS,
    stream: false,
    system: EXTRACTION_SYSTEM_PROMPT,
    messages: [
      {
        role: "user",
        content: `Here is the raw extracted text of a screenplay PDF. Normalise it to Final Draft format and return the structured JSON described in your instructions. Raw script text:\n\n${scriptText}`,
      },
    ],
  };

  const response = await fetch(`${workerBaseUrl.replace(/\/$/, "")}/chat`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `Parser returned ${response.status}${detail ? `: ${detail.slice(0, 200)}` : ""}`,
    );
  }
  const payload = (await response.json()) as {
    content?: Array<{ type?: string; text?: string }>;
  };
  const text = (payload.content ?? [])
    .filter((block) => block.type === "text")
    .map((block) => block.text ?? "")
    .join("");
  if (text.trim().length === 0) {
    throw new Error("Parser returned an empty response.");
  }
  return parseModelJson(text);
}

/** Count the scenes the parser actually produced (heading or body). */
export function countParsedScenes(parsed: ParsedScript): number {
  return parsed.scenes.filter(
    (s) => (s.body ?? "").trim().length > 0 || (s.heading ?? "").trim().length > 0,
  ).length;
}

/**
 * Turn a parser-shaped body ("NAME: dialogue" + action beats) into clean
 * structured Final-Draft elements: a slug from the heading, then each
 * "NAME: line" split into a character cue + dialogue, action otherwise.
 */
function sceneToElements(
  heading: string,
  body: string,
): Array<{ type: import("./scriptModel").ElementType; text: string }> {
  const out: Array<{ type: import("./scriptModel").ElementType; text: string }> = [];
  if (heading.trim()) out.push({ type: "slug", text: heading.trim() });
  for (const rawLine of body.split("\n")) {
    const line = rawLine.replace(/\s+$/, "");
    if (!line.trim()) continue;
    const speech = /^\s*([A-Za-z][A-Za-z0-9 .'()_-]{0,38}?)\s*:\s*(.*)$/.exec(line);
    if (speech && speech[1] === speech[1].toUpperCase()) {
      out.push({ type: "character", text: speech[1].trim() });
      if (speech[2].trim()) out.push({ type: "dialogue", text: speech[2].trim() });
      continue;
    }
    // Fall back to the generic classifier (slug/transition/paren/action).
    const els = bodyToElements(line, []);
    out.push(...els.map((e) => ({ type: e.type, text: e.text })));
  }
  return out;
}

/** Build the atomic store payload from a ParsedScript. */
export function buildIngestPayload(
  filename: string,
  parsed: ParsedScript,
): ParsedScriptIngest {
  const scenes = parsed.scenes
    .filter((s) => (s.body ?? "").trim().length > 0 || (s.heading ?? "").trim().length > 0)
    .map((scene, index) => {
      const heading = (scene.heading ?? `SCENE ${index + 1}`).trim() || `SCENE ${index + 1}`;
      const elements = sceneToElements(heading, (scene.body ?? "").trim());
      // Derive the scene's location from its slug heading.
      const slugMatch = /^\s*(INT|EXT|INT\/EXT|I\/E|EST)[.\s]+([^-–—]+)/i.exec(heading);
      const locationName = slugMatch ? slugMatch[2].trim().replace(/[.\s]+$/, "") : null;
      return {
        heading,
        elements: elements.map((e) => ({ ...e, id: "" })),
        body: elements.map((e) => e.text).join("\n"),
        locationName,
      };
    });

  return {
    filename,
    scenes,
    characters: parsed.characters
      .map((c) => ({ name: (c.name ?? "").trim(), description: (c.description ?? "").trim() }))
      .filter((c) => c.name.length > 0),
    locations: parsed.locations
      .map((l) => ({ name: (l.name ?? "").trim(), description: (l.description ?? "").trim() }))
      .filter((l) => l.name.length > 0),
    assets: parsed.assets
      .map((a) => ({
        name: (a.name ?? "").trim(),
        type: normalizeAssetType(a.type),
        description: (a.description ?? "").trim(),
      }))
      .filter((a) => a.name.length > 0),
  };
}

/**
 * Atomically ingest a parsed script into the store, returning the counts
 * of NEW entities created (deduped against existing state) for the
 * summary card.
 */
export function ingestParsedIntoStore(
  store: StudioStore,
  filename: string,
  parsed: ParsedScript,
): IngestCounts {
  const payload = buildIngestPayload(filename, parsed);
  const beforeChars = store.get("characters").length;
  const beforeLocs = store.get("locations").length;
  const beforeAssets = store.get("assets").length;
  store.ingestParsedScript(payload);
  return {
    scenes: payload.scenes.length,
    characters: Math.max(0, store.get("characters").length - beforeChars),
    locations: Math.max(0, store.get("locations").length - beforeLocs),
    assets: Math.max(0, store.get("assets").length - beforeAssets),
  };
}

// --------------------------------------------------------------------
// Validation
// --------------------------------------------------------------------

export function looksLikePdf(file: File): boolean {
  const name = file.name.toLowerCase();
  return file.type === "application/pdf" || name.endsWith(".pdf");
}

function parseModelJson(raw: string): ParsedScript {
  const jsonText = extractJsonObject(raw);
  if (!jsonText) throw new Error("Koko's reply wasn't valid JSON.");
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonText);
  } catch {
    throw new Error("Koko's reply wasn't valid JSON.");
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("Koko's reply wasn't a JSON object.");
  }
  const obj = parsed as Record<string, unknown>;
  return {
    scenes: asObjectArray(obj.scenes),
    characters: asObjectArray(obj.characters),
    locations: asObjectArray(obj.locations),
    assets: asObjectArray(obj.assets),
  } as ParsedScript;
}

function extractJsonObject(raw: string): string | null {
  const start = raw.indexOf("{");
  if (start === -1) return null;
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < raw.length; i += 1) {
    const ch = raw[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === "\\") escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === "{") depth += 1;
    else if (ch === "}") {
      depth -= 1;
      if (depth === 0) return raw.slice(start, i + 1);
    }
  }
  return null;
}

function asObjectArray(value: unknown): Array<Record<string, never>> {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is Record<string, never> => !!entry && typeof entry === "object",
  );
}

function normalizeAssetType(type: string | undefined): StudioAsset["type"] {
  const allowed: NonNullable<StudioAsset["type"]>[] = [
    "prop",
    "vehicle",
    "set_piece",
    "wardrobe",
    "other",
  ];
  const normalized = (type ?? "").trim().toLowerCase().replace(/\s+/g, "_");
  return (allowed as string[]).includes(normalized)
    ? (normalized as StudioAsset["type"])
    : "prop";
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
}

export function toUserMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong reading the script. Try again or skip and draft by hand.";
}

/** Resolve the worker base URL the same way main.ts does. */
export function resolveWorkerBaseUrl(): string {
  return (
    (typeof window !== "undefined" && window.__KOKO_CONFIG__?.api) ||
    document.getElementById("koko")?.getAttribute("api") ||
    ""
  );
}
